import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { ShieldCheck, ArrowRight, Building2, Landmark, Globe, MapPin, Languages, CreditCard } from "lucide-react";

/**
 * Africa · Country Landing — reads db.countries live and explains the market
 * BEFORE routing to the verification request form. Zimbabwe is redirected
 * to its dedicated subdomain (per product policy).
 */
export default function AfricaCountryLanding() {
  const { iso } = useParams();
  const [c, setC] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      // Zimbabwe → dedicated portal
      if ((iso || "").toUpperCase() === "ZW") {
        window.location.href = "https://zimbabwe.thomverify.com";
        return;
      }
      try {
        const r = await api.get(`/countries/${(iso || "").toUpperCase()}`);
        setC(r.data);
      } catch {
        setNotFound(true);
      }
    })();
  }, [iso]);

  if (notFound) return (
    <div>
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <h1 className="display text-3xl">Country not yet configured</h1>
        <p className="text-sm text-muted-foreground mt-2">This market has not been declared on the platform. Contact <a href="mailto:partnerships@thomverify.africa" className="text-primary">partnerships@thomverify.africa</a> to request rollout.</p>
        <Link to="/africa/countries" className="inline-block mt-6"><Button variant="outline">Back to country list</Button></Link>
      </div>
      <Footer />
    </div>
  );

  if (!c) return <div className="p-16 text-center text-muted-foreground">Loading country configuration…</div>;

  const accent = c.brand?.accent || "#B08C4A";
  const tagline = c.brand?.tagline;
  const services = c.verification_services || [];
  const gateways = c.gateways || [];
  const languages = c.languages || [];
  const cities = c.cities || [];
  const escrow = c.escrow_enabled;
  const active = c.is_active !== false;

  return (
    <div>
      <Navbar />

      {/* Hero */}
      <section className="border-b border-border relative overflow-hidden" style={{ background: `linear-gradient(135deg, #0A0A0A 0%, #111 100%)` }}>
        {c.brand?.hero_image_url && (
          <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{ backgroundImage: `url(${c.brand.hero_image_url})` }} data-testid="country-hero-image" />
        )}
        <div className="max-w-[1200px] mx-auto px-6 py-14 grid md:grid-cols-[1fr_auto] gap-8 items-start relative">
          <div className="text-white">
            <div className="mono text-[11px] uppercase tracking-widest" style={{ color: accent }}>THOM Verify™ Africa · Country Deployment</div>
            <h1 className="display text-5xl mt-2" data-testid="country-hero-name">{c.name}</h1>
            <div className="mono text-[11px] uppercase tracking-widest text-white/60 mt-2">{c.iso} · {c.currency}{c.tax_rule ? ` · ${c.tax_rule}` : ""}</div>
            {tagline && <p className="text-lg text-white/85 mt-4 max-w-2xl">{tagline}</p>}
            {!active && <div className="mt-4 inline-block mono uppercase text-[11px] tracking-widest px-2 py-1 bg-red-500/20 text-red-200 rounded-sm" data-testid="country-inactive">Coming soon · Not yet live</div>}
          </div>
          <div className="border border-white/10 rounded-sm p-5 bg-white/5 backdrop-blur min-w-[280px]">
            <div className="kicker text-white/60">Local operating status</div>
            <div className="flex items-center gap-2 mt-2 text-white">
              <div className={`h-2 w-2 rounded-full ${active ? "bg-emerald-400" : "bg-amber-400"}`} />
              <span className="font-semibold">{active ? "Live · accepting requests" : "In deployment"}</span>
            </div>
            <div className="mono text-[10.5px] uppercase tracking-widest text-white/50 mt-3">Full portal</div>
            <div className="text-sm text-white/90 mt-1">Hosted via the Africa continental platform</div>
          </div>
        </div>
      </section>

      {/* Explain — service availability + industries + how it works */}
      <section className="max-w-[1200px] mx-auto px-6 py-12 grid md:grid-cols-3 gap-6">
        <InfoCard title="Service availability" Icon={ShieldCheck}>
          {services.length === 0 ? (
            <div className="text-sm text-muted-foreground">Standard THOM Verify™ inspection scope.</div>
          ) : (
            <ul className="mt-2 space-y-1.5">
              {services.map(s => (
                <li key={s} className="text-sm flex items-center gap-2" data-testid={`country-service-${s}`}>
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: accent }} /> {formatService(s)}
                </li>
              ))}
            </ul>
          )}
        </InfoCard>

        <InfoCard title="Industries supported" Icon={Building2}>
          <ul className="mt-2 space-y-1.5 text-sm">
            <li>Automotive · Vehicle verification</li>
            <li>Real estate · Property verification</li>
            <li>Mining · Concessions & assay</li>
            <li>Commodities · Consignment quality</li>
            <li>Agriculture · Cold-chain & grading</li>
            <li>Industrial equipment · Machinery</li>
            <li>Government & banking collateral</li>
            <li>Insurance claims · Legal matters</li>
          </ul>
        </InfoCard>

        <InfoCard title="How verification works" Icon={ArrowRight}>
          <ol className="mt-2 space-y-1.5 text-sm list-decimal list-inside text-muted-foreground">
            <li><span className="text-foreground">Submit request</span> — asset details + scope</li>
            <li><span className="text-foreground">Field inspection</span> — independent inspector visit</li>
            <li><span className="text-foreground">Legal & registry checks</span> — title + police clearance</li>
            <li><span className="text-foreground">Review & approval</span> — authorised reviewer signs off</li>
            <li><span className="text-foreground">Certificate & badge</span> — PDF + live badge issued</li>
            <li><span className="text-foreground">Digital Passport</span> — lifetime asset history</li>
          </ol>
        </InfoCard>
      </section>

      {/* Facts */}
      <section className="max-w-[1200px] mx-auto px-6 pb-12 grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Fact label="Escrow module" value={escrow ? "Enabled" : "Manual only"} Icon={Landmark} testid="country-escrow" />
        <Fact label="Registration authority" value={c.registration_authority || "—"} Icon={ShieldCheck} testid="country-reg" />
        <Fact label="Police database" value={c.police_database || "—"} Icon={Globe} testid="country-police" />
        <Fact label="VIN / identifier format" value={c.vin_hint || "17-char ISO 3779"} Icon={ShieldCheck} testid="country-vin" />
        <Fact label="Inspection cities" value={cities.slice(0, 4).join(", ") + (cities.length > 4 ? ` +${cities.length - 4}` : "") || "—"} Icon={MapPin} testid="country-cities" />
        <Fact label="Local languages" value={languages.join(" · ").toUpperCase() || "EN"} Icon={Languages} testid="country-languages" />
        <Fact label="Payment gateways" value={`${gateways.length} declared`} sub={gateways.slice(0, 3).join(", ") + (gateways.length > 3 ? "…" : "")} Icon={CreditCard} testid="country-gateways" />
        <Fact label="Currency & tax" value={`${c.currency} · ${c.tax_rule || "—"}`} Icon={CreditCard} testid="country-currency" />
        <Fact label="Local contact" value={c.contact?.email || "operations@thomverify.africa"} sub={c.contact?.phone || "+263 (0) 24 000 0000"} Icon={Building2} testid="country-contact" />
        <Fact label="Turnaround" value={c.turnaround?.standard || "3–5 business days"} sub={c.turnaround?.enhanced || "Enhanced 24–48 h"} Icon={ArrowRight} testid="country-turnaround" />
      </section>

      {/* CTA */}
      <section className="border-t border-border bg-muted/30">
        <div className="max-w-[1200px] mx-auto px-6 py-14 grid md:grid-cols-[1fr_auto] items-center gap-6">
          <div>
            <div className="kicker">Ready to verify an asset in {c.name}?</div>
            <h2 className="display text-3xl mt-1">Request a verification</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Your request is routed to our {c.name} operations team. Verification fee, escrow and payment gateways are pre-configured
              for this market — no admin work required.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to={`/africa/countries/${c.iso}/request`}>
              <Button size="lg" disabled={!active} data-testid="country-cta-request">
                {active ? <>Request verification <ArrowRight className="h-4 w-4 ml-2" /></> : "Coming soon"}
              </Button>
            </Link>
            <a href="mailto:partnerships@thomverify.africa"><Button size="lg" variant="outline">Talk to partnerships</Button></a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const InfoCard = ({ title, Icon, children }) => (
  <div className="border border-border bg-card p-5">
    <div className="flex items-center gap-2 kicker"><Icon className="h-3.5 w-3.5" /> {title}</div>
    {children}
  </div>
);

const Fact = ({ label, value, sub, Icon, testid }) => (
  <div className="border border-border bg-card p-4" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className="min-w-0">
        <div className="kicker">{label}</div>
        <div className="text-sm mt-1 font-medium truncate">{value}</div>
        {sub && <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{sub}</div>}
      </div>
      {Icon && <Icon className="h-4 w-4 text-primary shrink-0" />}
    </div>
  </div>
);

const formatService = (key) => ({
  autoverify:      "AutoVerify™ — Vehicle inspection & authenticity",
  propertyverify:  "PropertyVerify™ — Real estate title & structure",
  equipmentverify: "EquipmentVerify™ — Industrial equipment & machinery",
  businessverify:  "BusinessVerify™ — Supplier / KYB / trade partner",
  luxuryverify:    "LuxuryVerify™ — High-value assets & collectibles",
  fleetverify:     "FleetVerify™ — Commercial fleet & logistics",
}[key] || key);
